import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { getFirestore, collection, getDocs } from '@firebase/firestore'
import { app } from '../../firebase.js'
import {
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip
} from 'recharts'
import Table from 'react-bootstrap/Table'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import Spinner from 'react-bootstrap/Spinner'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowsRotate, faChartPie, faMagnifyingGlass, faUsers } from '@fortawesome/free-solid-svg-icons'
import LeftBar from '../../Components/LeftBar'
import Dashnav from '../../Components/Dashnav'
import '../../Pages/dash.css'

const countFormatter = new Intl.NumberFormat('en-KE')

const formatCount = (value) => countFormatter.format(Number(value || 0))

const normalizeText = (value) => String(value || '').trim()

const normalizeStatus = (value) => normalizeText(value).toLowerCase()

const toTitleCase = (value) => {
  const text = normalizeText(value)
  if (!text) return 'Unknown'

  return text
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => {
      const lower = word.toLowerCase()
      return lower.charAt(0).toUpperCase() + lower.slice(1)
    })
    .join(' ')
}

const getStatusCategory = (status) => {
  const normalized = normalizeStatus(status)

  if (['student', 'active'].includes(normalized)) {
    return 'Active'
  }

  if (['completed'].includes(normalized)) {
    return 'Completed'
  }

  if (['deferred', 'dropped', 'expelled', 'inactive', 'did not join'].includes(normalized)) {
    return 'Drop-off'
  }

  return 'Other'
}

const getStatusColor = (category, index) => {
  if (category === 'Active') return '#0f766e'
  if (category === 'Drop-off') return '#ef4444'
  if (category === 'Completed') return '#3b82f6'
  if (category === 'Other') return '#94a3b8'

  const palette = ['#0f766e', '#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444', '#14b8a6']
  return palette[index % palette.length]
}

const DropOffTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) {
    return null
  }

  const row = payload[0]?.payload || {}

  return (
    <div className="dropOffTooltip">
      <strong>{row.label || row.status}</strong>
      <div className="dropOffTooltipRow">
        <span>Count</span>
        <strong>{formatCount(row.count)}</strong>
      </div>
      <div className="dropOffTooltipRow">
        <span>Share</span>
        <strong>{row.shareLabel}</strong>
      </div>
      <div className="dropOffTooltipRow">
        <span>Category</span>
        <strong>{row.category}</strong>
      </div>
    </div>
  )
}

function DropOffAnalysis() {
  const db = getFirestore(app)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [rows, setRows] = useState([])

  const fetchData = useCallback(async (options = {}) => {
    const initialLoad = options.initial === true

    if (initialLoad) {
      setLoading(true)
    } else {
      setRefreshing(true)
    }

    setError('')

    try {
      const snapshot = await getDocs(collection(db, 'users'))
      const statusCounts = {}

      snapshot.forEach((docItem) => {
        const data = docItem.data()
        const status = normalizeText(data.userStatus) || 'inactive'
        statusCounts[status] = (statusCounts[status] || 0) + 1
      })

      const formattedRows = Object.keys(statusCounts)
        .map((status) => {
          const count = statusCounts[status]
          const label = toTitleCase(status)
          const category = getStatusCategory(status)

          return {
            status,
            label,
            count,
            category
          }
        })
        .sort((first, second) => {
          if (second.count !== first.count) {
            return second.count - first.count
          }

          return first.label.localeCompare(second.label)
        })

      setRows(formattedRows)
    } catch (fetchError) {
      console.error('Failed to load drop-off analysis:', fetchError)
      setRows([])
      setError('We could not load the drop-off analysis right now. Please try again.')
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [db])

  useEffect(() => {
    fetchData({ initial: true })
  }, [fetchData])

  const filteredRows = useMemo(() => {
    const search = searchTerm.trim().toLowerCase()

    if (!search) {
      return rows
    }

    return rows.filter((row) => {
      return [
        row.status,
        row.label,
        row.category
      ].some((value) => String(value || '').toLowerCase().includes(search))
    })
  }, [rows, searchTerm])

  const totalUsers = useMemo(() => {
    return rows.reduce((sum, row) => sum + row.count, 0)
  }, [rows])

  const categoryTotals = useMemo(() => {
    return rows.reduce((accumulator, row) => {
      accumulator[row.category] = (accumulator[row.category] || 0) + row.count
      return accumulator
    }, {})
  }, [rows])

  const chartData = useMemo(() => {
    const topRows = filteredRows.slice(0, 6)
    const others = filteredRows.slice(6).reduce((sum, row) => sum + row.count, 0)
    const baseData = topRows.map((row) => ({
      ...row,
      shareLabel: totalUsers > 0 ? `${((row.count / totalUsers) * 100).toFixed(1)}%` : '0.0%'
    }))

    if (others > 0) {
      baseData.push({
        status: 'other-statuses',
        label: 'Other statuses',
        count: others,
        category: 'Other',
        shareLabel: totalUsers > 0 ? `${((others / totalUsers) * 100).toFixed(1)}%` : '0.0%'
      })
    }

    return baseData
  }, [filteredRows, totalUsers])

  const chartColors = useMemo(() => {
    return chartData.map((row, index) => getStatusColor(row.category, index))
  }, [chartData])

  const topRows = rows.slice(0, 5)
  const topStatus = rows[0] || null
  const dropOffTotal = categoryTotals['Drop-off'] || 0
  const activeTotal = categoryTotals['Active'] || 0
  const completedTotal = categoryTotals['Completed'] || 0
  const hasRows = filteredRows.length > 0
  const emptyMessage = rows.length
    ? 'No statuses match the current search.'
    : 'No drop-off records were found yet.'

  return (
    <div className="mainContainer dropOffAnalysisPage">
      <LeftBar />
      <div className="theMainContainer">
        <Dashnav />

        <div className="dropOffAnalysisContent">
          <div className="appsHeader">
            <div className="attendanceHeaderCopy">
              <p className="appsKicker">Data Analytics</p>
              <h4>Drop-off Analysis</h4>
              <p className="appsSubtext">
                See how student statuses are distributed, where most drop-off signals are concentrated, and which status currently leads the system.
              </p>
            </div>

            <div className="appsHeaderStats">
              <div className="appsStatChip">
                <span>Total records</span>
                <strong>{formatCount(totalUsers)}</strong>
              </div>
              <div className="appsStatChip">
                <span>Drop-off</span>
                <strong>{formatCount(dropOffTotal)}</strong>
              </div>
              <div className="appsStatChip">
                <span>Active</span>
                <strong>{formatCount(activeTotal)}</strong>
              </div>
              <div className="appsStatChip">
                <span>Top status</span>
                <strong>{topStatus ? topStatus.label : '—'}</strong>
              </div>
            </div>
          </div>

          <div className="appsFiltersCard dropOffFiltersCard">
            <div className="appsSearch input-group dropOffSearch">
              <span className="input-group-text">
                <FontAwesomeIcon icon={faMagnifyingGlass} />
              </span>
              <Form.Control
                type="text"
                placeholder="Search status or category..."
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
              />
            </div>
            <div className="dropOffFiltersMeta">
              <span>{formatCount(filteredRows.length)} statuses visible</span>
              <span>{formatCount(chartData.length)} slices shown in the chart</span>
            </div>
            <Button variant="outline-dark" onClick={() => fetchData()} disabled={loading || refreshing}>
              <FontAwesomeIcon icon={faArrowsRotate} className="me-2" />
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
          </div>

          {error && (
            <div className="incomeStateCard mb-3">
              <p>{error}</p>
              <Button variant="outline-dark" onClick={() => fetchData()} disabled={refreshing}>
                <FontAwesomeIcon icon={faArrowsRotate} className="me-2" />
                Try again
              </Button>
            </div>
          )}

          <div className="dropOffGrid">
            <div className="appsTableCard dropOffChartCard">
              <div className="appsTableHeader">
                <h6>
                  <FontAwesomeIcon icon={faChartPie} className="me-2" />
                  Status mix
                </h6>
                <span>Ranked by volume</span>
              </div>
              <div className="dropOffChartWrap">
                {loading ? (
                  <div className="incomeStateCard">
                    <Spinner animation="border" role="status">
                      <span className="visually-hidden">Loading drop-off analysis...</span>
                    </Spinner>
                    <p>Loading drop-off analysis...</p>
                  </div>
                ) : chartData.length ? (
                  <div className="dropOffChartBox">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={chartData}
                          dataKey="count"
                          nameKey="label"
                          cx="50%"
                          cy="50%"
                          innerRadius={88}
                          outerRadius={150}
                          paddingAngle={3}
                          label={({ label, percent }) => `${label} ${((percent || 0) * 100).toFixed(0)}%`}
                          labelLine={false}
                        >
                          {chartData.map((entry, index) => (
                            <Cell key={`dropoff-cell-${entry.status}-${index}`} fill={chartColors[index]} />
                          ))}
                        </Pie>
                        <Tooltip content={<DropOffTooltip />} />
                        <Legend verticalAlign="bottom" height={42} iconType="circle" wrapperStyle={{ fontSize: '12px', color: '#64748b' }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="incomeStateCard">
                    <p>{emptyMessage}</p>
                  </div>
                )}
              </div>
            </div>

            <div className="appsInsightsCard dropOffInsightsCard">
              <div className="appsInsightsHeader">
                <h6>
                  <FontAwesomeIcon icon={faUsers} className="me-2" />
                  Key signals
                </h6>
                <span>{formatCount(topRows.length)} shown</span>
              </div>

              <div className="appsInsightsList">
                {topRows.length ? (
                  topRows.map((row) => (
                    <div className="appsInsightsItem" key={row.status}>
                      <div>
                        <strong>{row.label}</strong>
                        <span>{row.category} status</span>
                      </div>
                      <small>{formatCount(row.count)}</small>
                    </div>
                  ))
                ) : (
                  <div className="incomeStateCard">
                    <p>No status signals match the current search.</p>
                  </div>
                )}
              </div>

              <div className="dropOffMiniStats">
                <div className="dropOffMiniStat">
                  <span>Drop-off share</span>
                  <strong>{totalUsers > 0 ? `${((dropOffTotal / totalUsers) * 100).toFixed(1)}%` : '0.0%'}</strong>
                </div>
                <div className="dropOffMiniStat">
                  <span>Completed share</span>
                  <strong>{totalUsers > 0 ? `${((completedTotal / totalUsers) * 100).toFixed(1)}%` : '0.0%'}</strong>
                </div>
              </div>

              <p className="appsInsightsMore">
                Search a status to narrow the view, or refresh after records change in Firebase.
              </p>
            </div>
          </div>

          <div className="appsTableCard dropOffTableCard">
            <div className="appsTableHeader">
              <h6>
                <FontAwesomeIcon icon={faUsers} className="me-2" />
                Status summary
              </h6>
              <span>{formatCount(filteredRows.length)} results</span>
            </div>
            <div className="appsTableWrap">
              {loading ? (
                <div className="incomeStateCard">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading status summary...</span>
                  </Spinner>
                  <p>Loading status summary...</p>
                </div>
              ) : (
                <Table hover responsive className="appsTable dropOffTable">
                  <thead>
                    <tr>
                      <th>Status</th>
                      <th>Count</th>
                      <th>Share</th>
                      <th>Category</th>
                      <th>Volume</th>
                    </tr>
                  </thead>
                  <tbody>
                    {hasRows ? (
                      filteredRows.map((row) => {
                        const share = totalUsers > 0 ? (row.count / totalUsers) * 100 : 0
                        const progress = totalUsers > 0 ? (row.count / rows[0].count) * 100 : 0

                        return (
                          <tr key={row.status}>
                            <td>
                              <div className="dropOffStatusCell">
                                <strong>{row.label}</strong>
                                <span>{row.status}</span>
                              </div>
                            </td>
                            <td>{formatCount(row.count)}</td>
                            <td>{share.toFixed(1)}%</td>
                            <td>
                              <span className={`dropOffCategoryBadge dropOffCategoryBadge--${row.category.toLowerCase().replace(/\s+/g, '-')}`}>
                                {row.category}
                              </span>
                            </td>
                            <td>
                              <div className="dropOffTrack" aria-hidden="true">
                                <span className="dropOffTrackFill" style={{ width: `${Math.min(progress, 100)}%` }} />
                              </div>
                            </td>
                          </tr>
                        )
                      })
                    ) : (
                      <tr>
                        <td colSpan="5" className="appsEmptyState">
                          {emptyMessage}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default DropOffAnalysis
